import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { earningsService } from '../../services/earningsService';
import { EarningsPlay } from '../../types/earnings';
import { fmt, gradeColor } from '../../utils/formatters';

const EarningsList: React.FC = () => {
  const [plays, setPlays] = useState<EarningsPlay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    earningsService.listPlays()
      .then(setPlays)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

  const openPlays = plays.filter((p) => p.status === 'open');
  const archivedPlays = plays.filter((p) => p.status === 'archived');

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>Earnings Plays</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/discover?segment=earnings')}
        >
          New Play
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!plays.length ? (
        <Alert severity="info">
          No earnings plays yet. Go to Discover &rarr; Earnings to find upcoming events.
        </Alert>
      ) : (
        <>
          <PlayTable plays={openPlays} navigate={navigate} label="Active Plays" emptyLabel="No active plays." />
          {archivedPlays.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <PlayTable plays={archivedPlays} navigate={navigate} label="Archived Plays" />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const PlayTable: React.FC<{
  plays: EarningsPlay[];
  navigate: ReturnType<typeof useNavigate>;
  label: string;
  emptyLabel?: string;
}> = ({ plays, navigate, label, emptyLabel }) => {
  if (!plays.length && !emptyLabel) return null;

  return (
    <>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>{label}</Typography>
      {!plays.length ? (
        <Alert severity="info" variant="outlined">{emptyLabel}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                <TableCell>Ticker</TableCell>
                <TableCell>Earnings Date</TableCell>
                <TableCell align="center">Grade</TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Trades</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">P&amp;L</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plays.map((p) => (
                <TableRow
                  key={p.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(
                    p.status === 'archived'
                      ? `/discover/earnings/${p.ticker}?date=${p.earnings_date}&play=${p.id}`
                      : `/discover/earnings/${p.ticker}?date=${p.earnings_date}`
                  )}
                >
                  <TableCell><Typography fontWeight={700}>{p.ticker}</Typography></TableCell>
                  <TableCell>{fmt.date(p.earnings_date)}</TableCell>
                  <TableCell align="center">
                    {p.grade ? (
                      <Chip
                        label={p.grade}
                        size="small"
                        sx={{ bgcolor: gradeColor(p.grade), color: 'white', fontWeight: 700 }}
                      />
                    ) : (
                      <Typography color="text.secondary" variant="body2">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">
                      {p.open_trade_count > 0
                        ? `${p.open_trade_count} open / ${p.total_trade_count}`
                        : `${p.total_trade_count}`
                      }
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={p.status === 'archived' ? 'Archived' : p.open_trade_count > 0 ? 'Open' : 'Closed'}
                      size="small"
                      color={p.open_trade_count > 0 ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {p.total_pnl != null
                      ? (
                        <Typography
                          color={Number(p.total_pnl) >= 0 ? 'success.main' : 'error.main'}
                          fontWeight={600}
                        >
                          {Number(p.total_pnl) >= 0 ? '+' : ''}{fmt.currency(p.total_pnl)}
                        </Typography>
                      )
                      : <Typography color="text.secondary">—</Typography>
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default EarningsList;
